/* eslint-disable */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import Modal from "react-modal";
import { Spin } from "antd";
import { Search, Check, X } from "lucide-react";
import { serverTimestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { addMultipleProducts } from "../../../../redux/products/productsActions";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
Modal.setAppElement("#root");

const MySwal = withReactContent(Swal);

const ProductExportModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [link, setLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [storeType, setStoreType] = useState("");
  const [manualSelectVisible, setManualSelectVisible] = useState(false);
  const store = useSelector((state) => state.StoreReducer);
  const { t, i18n } = useTranslation();
    const currencyMap = {
    دج: {
      ar: "دج",
      fr: "DA",
      en: "DZD",
    },
  };
  const localizedCurrency =
    currencyMap[store.currencySymbol]?.[i18n.language] || store.currencySymbol;
  const isRTL = i18n.dir() === 'rtl';

  // Custom styles for the modal
  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "800px",
      maxWidth: "90%",
      borderRadius: "12px",
      padding: "0",
      border: "1px solid #e5e7eb",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      maxHeight: "90vh",
      overflow: "hidden",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 99999,
    },
  };

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setLink("");
      setError(null);
      setProducts([]);
      setSelectedProducts([]);
      setIsLoading(false);
      setIsSubmitting(false);
      setHasFetched(false);
      setStoreType("");
      setManualSelectVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!link.trim()) {
      setManualSelectVisible(false);
      setStoreType("");
      return;
    }

    const hostDomain = extractHost(link);
    const autoType = checkStoreType(hostDomain);

    if (!autoType) {
      setManualSelectVisible(true); // Show select
      setStoreType(""); // User will choose
    } else {
      setManualSelectVisible(false); // Hide select
      setStoreType(autoType); // Set type automatically
    }
  }, [link]);

  // Handle link input change
  const handleLinkChange = (e) => {
    setLink(e.target.value);
    setError(null);
  };

  // Check store type
  const checkStoreType = (url) => {
    if (url.includes("youcan.store")) return "youcan";
    if (url.includes("myshopify.com")) return "shopify";
    return null; // User will choose manually
  };

  // Extract host from URL
  const extractHost = (url) => {
    let cleanedUrl = url.trim();
    cleanedUrl = cleanedUrl.replace(/^https?:\/\//, "");
    return cleanedUrl.split("/")[0];
  };

  // Fetch products from the API
  const fetchProducts = async () => {
    if (!link.trim()) {
      setError(t("ui.storeUrlRequired"));
      return;
    }

    // Validate store type is selected when manual selection is visible
    if (manualSelectVisible && !storeType) {
      setError(t("ui.storeTypeRequired"));
      return;
    }

    const hostDomain = extractHost(link);
    let detectedStoreType = checkStoreType(hostDomain);

    // If not automatically detected, use the type selected by the user
    if (!detectedStoreType && storeType) {
      detectedStoreType = storeType;
    }

    setIsLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      const apiUrl = `https://backend.foorweb.shop/api/getProductsByLink?host=${hostDomain}&type=${detectedStoreType}`;
      const response = await axios.get(apiUrl);

      // console.log("WooCommerce Products:", response.data);
      if (response.data?.data && Array.isArray(response.data.data)) {
        setProducts(response.data.data); // Handle nested array
      }
      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        setProducts(response.data);
        // Force selectedProducts to be empty on new fetch
        setSelectedProducts([]);
      } else {
        setProducts([]);
        setError(t("Products.Table.empty"));
      }
    } catch (err) {
      console.error("Error fetching products:", err);

      if (err.response && err.response.status === 500) {
        setError(t("ui.storeUnsupported"));
      } else {
        setError(t("ui.productFetchError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Extracts the first 4 words from a product name and converts it to a URL-friendly string
   * @param {string} productName - The full product name
   * @returns {string} - URL-friendly string with the first 4 words
   */
  const extractUrlFromName = (productName) => {
    if (!productName) return "";

    // Split the product name into words
    const words = productName.split(" ");

    // Take the first 4 words (or all words if less than 4)
    const firstFourWords = words.slice(0, 4);

    // Join the words with hyphens and make them URL-friendly
    let urlString = firstFourWords
      .join("")
      // Remove special characters
      .replace(/[^\w\s-]/g, "")
      // Replace spaces with hyphens
      .replace(/\s+/g, "-")
      // Convert to lowercase
      .toLowerCase()
      // Remove multiple consecutive hyphens
      .replace(/-+/g, "-")
      // Remove hyphens from beginning and end
      .replace(/^-+|-+$/g, "");

    return urlString;
  };

  // Transform product based on store type
  const transformProduct = (product) => {
    if (storeType === "youcan") {
      return transformYouCanProduct(product);
    } else if (storeType === "shopify") {
      return transformShopifyProduct(product);
    } else if (storeType === "woocommerce") {
      return transformWooCommerceProduct(product);
    }
    return null;
  };
  // Transform YouCan product
  const transformYouCanProduct = (youcanProduct) => {
    // Extract gallery images if available
    const gallery = youcanProduct.images
      ? youcanProduct.images
        .map((img) => img.url || img.variations?.original || "")
        .filter(Boolean)
      : [];

    // Generate URL-friendly string from product name
    const urlString = extractUrlFromName(youcanProduct.name);

    return {
      url: youcanProduct?.slug,
      name: youcanProduct.name || "",
      image: youcanProduct.thumbnail || (gallery.length > 0 ? gallery[0] : ""),
      review: "",
      price: String(youcanProduct.price || ""),
      oldPrice: String(youcanProduct.compare_at_price || ""),
      comment: "",
      qty: "",
      googleSheetP: "",
      googleSheetPActive: false,
      visits: 0,
      category_id: "كل المنتجات",
      short: "",
      delevery: false,
      upsell: false,
      sku: youcanProduct.slug || "",
      show: "1",
      options: [
        {
          title: "",
          color: "",
          option1: "",
          option2: "",
          option3: "",
          option4: "",
        },
      ],
      offers: [
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
      ],
      discount: {
        freeDelivery: false,
        activeStay: false,
        discountDelivery: 0,
        discountTotal: 0,
        seconds: 0,
      },
      gallery: gallery,
      description: [],
      textEditor: youcanProduct.description || "<p><br></p>",
      timeStamp: serverTimestamp(),
    };
  };

  // Transform Shopify product
  const transformShopifyProduct = (shopifyProduct) => {
    // Extract gallery images if available
    const gallery = shopifyProduct.images
      ? shopifyProduct.images.map((img) => img.src || "").filter(Boolean)
      : [];

    // Get price from first variant
    let productPrice = "";
    let productOldPrice = "";
    let productSku = "";
    if (shopifyProduct.variants && shopifyProduct.variants.length > 0) {
      const firstVariant = shopifyProduct.variants[0];
      productPrice = firstVariant.price || "";
      productSku = firstVariant.sku || "";
      productOldPrice = firstVariant.compare_at_price || "";
    }

    // Generate URL-friendly string from product name
    const urlString = extractUrlFromName(shopifyProduct.title);

    return {
      url: urlString,
      name: shopifyProduct.title,
      image: gallery.length > 0 ? gallery[0] : "",
      review: "",
      price: productPrice ? String(productPrice) : "",
      oldPrice: productOldPrice ? String(productOldPrice) : "",
      comment: "",
      qty: "",
      googleSheetP: "",
      googleSheetPActive: false,
      visits: 0,
      category_id: "كل المنتجات",
      short: "",
      delevery: false,
      upsell: false,
      sku: productSku || "",
      show: "1",
      options: [
        {
          title: "",
          color: "",
          option1: "",
          option2: "",
          option3: "",
          option4: "",
        },
      ],
      offers: [
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
      ],
      discount: {
        freeDelivery: false,
        activeStay: false,
        discountDelivery: 0,
        discountTotal: 0,
        seconds: 0,
      },
      gallery: gallery,
      description: [],
      textEditor: shopifyProduct.body_html || "<p><br></p>",
      timeStamp: serverTimestamp(),
    };
  };

  // Get product info for displaying in product cards for WooCommerce
  const getWooCommerceProductInfo = (product) => {
    return {
      name: product.name || "Untitled Product",
      price: product.prices?.price || product.price || "0",
      comparePrice:
        product.prices?.regular_price !== product.prices?.sale_price
          ? product.prices?.regular_price
          : product.regular_price || "",
      image:
        (product.images && product.images[0]?.src) || "/placeholder-image.png", // Fallback image
    };
  };

  // Transform WooCommerce product
  const transformWooCommerceProduct = (wooProduct) => {
    // console.log("Transforming WooCommerce Product:", wooProduct);

    // Extract gallery images if available
    const gallery = wooProduct.images
      ? wooProduct.images.map((img) => img.src || "").filter(Boolean)
      : [];

    // Get price from product - handle different price formats
    const productPrice = wooProduct.prices?.price || wooProduct.price || "";
    const productOldPrice =
      wooProduct.prices?.regular_price !== wooProduct.prices?.sale_price
        ? wooProduct.prices?.regular_price
        : wooProduct.regular_price || "";

    // Generate URL-friendly string from product name
    const urlString = extractUrlFromName(wooProduct.name);

    return {
      url: wooProduct.slug || urlString,
      name: wooProduct.name || "",
      image: gallery.length > 0 ? gallery[0] : "",
      review: "",
      price: productPrice ? String(productPrice) : "",
      oldPrice: productOldPrice ? String(productOldPrice) : "",
      comment: "",
      qty: "",
      googleSheetP: "",
      googleSheetPActive: false,
      visits: 0,
      category_id: "كل المنتجات",
      short: wooProduct.short_description || "",
      delevery: false,
      upsell: false,
      sku: wooProduct.sku || "",
      show: "1",
      options: [
        {
          title: "",
          color: "",
          option1: "",
          option2: "",
          option3: "",
          option4: "",
        },
      ],
      offers: [
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
        { title: "", qty: 0, price: 0 },
      ],
      discount: {
        freeDelivery: false,
        activeStay: false,
        discountDelivery: 0,
        discountTotal: 0,
        seconds: 0,
      },
      gallery: gallery,
      description: [],
      textEditor: wooProduct.description || "<p><br></p>",
      timeStamp: serverTimestamp(),
    };
  };

  const getProductInfo = (product) => {
    if (storeType === "youcan") {
      return {
        name: product.name || "",
        price: product.price || "",
        comparePrice: product.compare_at_price || "",
        image: product.thumbnail || "",
      };
    } else if (storeType === "shopify") {
      let productPrice = "";
      let productComparePrice = "";

      if (product.variants && product.variants.length > 0) {
        const firstVariant = product.variants[0];
        productPrice = firstVariant.price || "";
        productComparePrice = firstVariant.compare_at_price || "";
      }

      const image =
        product.images && product.images.length > 0
          ? product.images[0].src
          : "";

      return {
        name: product.title || "",
        price: productPrice,
        comparePrice: productComparePrice,
        image: image || "",
      };
    } else if (storeType === "woocommerce") {
      return getWooCommerceProductInfo(product);
    }

    return { name: "", price: "", comparePrice: "", image: "" };
  };

  // useEffect(() => {
  //   console.log("Current storeType:", storeType);
  // }, [storeType]);

  // Handle selection of a product
  const handleSelectProduct = (productIndex) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productIndex)) {
        return prev.filter((index) => index !== productIndex);
      } else {
        return [...prev, productIndex];
      }
    });
  };

  // Handle "Select All" action
  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((_, index) => index));
    }
  };

  // Handle submit action
  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      setError(t("digitalSections.messages.form.warningText"));
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the selected products using indices
      const productsToExport = selectedProducts.map((index) => products[index]);

      // Transform products to your Firebase format
      const transformedProducts = productsToExport
        .map((product) => transformProduct(product))
        .filter(Boolean);

      if (transformedProducts.length === 0) {
        throw new Error("لم يتم تحويل أي منتجات");
      }

      // Use the bulk import action to add all products at once
      const result = await dispatch(addMultipleProducts(transformedProducts));

      if (result) {
        // Show success message
        MySwal.fire({
          icon: "success",
          title: t("ui.importSuccess"),
          text: t("ui.productsImportedCount", { value0: (transformedProducts.length) }),
          confirmButtonColor: "#9b59bf",
        });

        // Close the modal and reset on success
        onClose();
        setSelectedProducts([]);
        setProducts([]);
        setLink("");
      } else {
        // Show error message with SweetAlert2
        MySwal.fire({
          icon: "error",
          title: t("error"),
          text: t("ui.productImportError"),
          confirmButtonColor: "#9b59bf",
        });

        setError(t("ui.productImportError"));
      }
    } catch (err) {
      console.error("Error exporting products:", err);

      // Show error message with SweetAlert2
      MySwal.fire({
        icon: "error",
        title: t("error"),
        text: t("ui.productImportError"),
        confirmButtonColor: "#9b59bf",
      });

      setError(t("ui.productImportError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel={t("Products.ImportModal.title")}
    >
      <div className="py-4 px-6">
        <div className="flex items-center justify-between mb-4 pb-2 border-b">
          <h2 className="text-[#9e65bb] font-bold text-xl flex-grow">
            {t("Products.ImportModal.title")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="text-[#9e65bb]" size={22} />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-3 font-medium">
            {t("Products.ImportModal.description")}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={t("Products.ImportModal.placeholder")}
              value={link}
              onChange={handleLinkChange}
              disabled={isLoading}
              className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-transparent"
            />
            <button
              onClick={fetchProducts}
              disabled={isLoading || (manualSelectVisible && !storeType)}
              className={`px-5 py-3 rounded-lg text-white font-medium flex items-center shadow-sm ${isLoading || (manualSelectVisible && !storeType)
                  ? "bg-[#9b59bf] opacity-70 cursor-not-allowed"
                  : "bg-[#9b59bf] hover:bg-[#814a9f] transition-colors"
                }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <Spin size="small" />
                  <span className="mr-2">
                    {t("Products.ImportModal.searching")}
                  </span>
                </div>
              ) : (
                <div className="flex items-center">
                  <Search size={18} className="ml-2" />
                  <span>{t("Products.ImportModal.search")}</span>
                </div>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-lg mb-5 flex items-center shadow-sm">
            <span className="mr-2 font-medium">{error}</span>
          </div>
        )}

        {manualSelectVisible && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              {t("Products.ImportModal.storeTypeLabel")}
            </label>
            <select
              value={storeType}
              onChange={(e) => setStoreType(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg p-3"
            >
              <option value="">
                {t("Products.ImportModal.storeTypePlaceholder")}
              </option>
              <option value="shopify">
                {t("Products.ImportModal.storeTypeOptions.shopify")}
              </option>
              <option value="woocommerce">
                {t("Products.ImportModal.storeTypeOptions.woocommerce")}
              </option>
            </select>
          </div>
        )}

        {products.length > 0 && (
          <div className="bg-white rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#9e65bb]">
                {t("Products.ImportModal.resultsFound", {
                  count: products.length,
                })}
              </h3>
              <button
                onClick={handleSelectAll}
                className="text-[#9b59bf] hover:text-[#814a9f] text-sm font-medium px-3 py-1 hover:bg-purple-50 rounded-md transition-colors"
              >
                {selectedProducts.length === products.length
                  ? t("Products.ImportModal.unselectAll")
                  : t("Products.ImportModal.selectAll")}
              </button>
            </div>

            <div
              className="max-h-96 overflow-y-auto mb-5 pr-2"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#d8b4fe #f5f5f5",
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((product, index) => {
                  const productInfo = getProductInfo(product);
                  return (
                    <div
                      key={index}
                      onClick={() => handleSelectProduct(index)}
                      className={`border rounded-lg overflow-hidden flex cursor-pointer transition-all hover:shadow-md ${selectedProducts.includes(index)
                          ? "border-[#9b59bf] bg-purple-50 shadow-sm"
                          : "border-gray-200 hover:border-[#9b59bf]"
                        }`}
                    >
                      <div className="flex-shrink-0 w-24 h-24 bg-gray-100">
                        {productInfo.image && (
                          <img
                            src={productInfo.image}
                            alt={productInfo.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-grow p-3 flex flex-col justify-between">
                        <div>
                          <h4 className="font-medium text-sm line-clamp-2 mb-1">
                            {productInfo.name}
                          </h4>
                          <div className="flex items-center text-sm gap-2">
                            <span className="font-bold text-[#9e65bb]">
                              {productInfo.price} {localizedCurrency}
                            </span>
                            {productInfo.comparePrice && (
                              <span className="text-gray-400 line-through">
                                {productInfo.comparePrice} {localizedCurrency}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center p-3">
                        <div
                          className={`h-6 w-6 rounded-full border flex items-center justify-center transition-colors ${selectedProducts.includes(index)
                              ? "bg-[#9b59bf] border-[#9b59bf]"
                              : "border-gray-300"
                            }`}
                        >
                          {selectedProducts.includes(index) && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 border-t pt-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("Products.ImportModal.cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={selectedProducts.length === 0 || isSubmitting}
                className={`px-5 py-2.5 rounded-lg text-white font-medium flex items-center shadow-sm ${selectedProducts.length === 0 || isSubmitting
                    ? "bg-[#9b59bf] opacity-70 cursor-not-allowed"
                    : "bg-[#9b59bf] hover:bg-[#814a9f] transition-colors"
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <Spin size="small" />
                    <span className="mr-2">
                      {t("Products.ImportModal.importing")}
                    </span>
                  </>
                ) : (
                  <>
                    <Check size={18} className="ml-2" />
                    <span>
                      {t("Products.ImportModal.importSelected", {
                        count: selectedProducts.length,
                      })}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {!isLoading && products.length === 0 && link && !error && hasFetched && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-gray-500 text-lg font-medium">
              {t("Products.ImportModal.notFound.title")}
            </div>
            <p className="text-gray-400 mt-2">
              {t("Products.ImportModal.notFound.description")}
            </p>
          </div>
        )}
      </div>
    </Modal>

  );
};

export default ProductExportModal;
